/**
 * useNewSupportMessages — client-side detector for new replies on a user's
 * support tickets.
 *
 * Why poll instead of SSE:
 *   The `/support/tickets/{id}/stream` endpoint exists (see supportSSE.ts)
 *   but is per-ticket — to detect new messages across every open ticket we
 *   would need N connections opened from `AppLayout`, plus a manifest call
 *   to know which IDs to subscribe to. Polling `/support/tickets/mine` once
 *   per 30s is cheaper, fail-soft, and reuses the existing React Query
 *   cache (`useMyTickets` already runs every 2min for the ticket list page;
 *   we shorten the interval globally to 30s when the feature is on).
 *
 * Heuristic:
 *   For each ticket the user reported, compare `updated_at` with the
 *   localStorage `lastSeen` mark (`support:lastSeen:{ticketId}`). If a
 *   ticket was updated *after* lastSeen AND its status is "open"-ish
 *   (i.e. not closed/resolved without further activity), surface a
 *   notification entry into `useSupportNotificationsStore`.
 *
 * Bootstrapping:
 *   The first poll after install would otherwise re-flash every existing
 *   open ticket as "new" because lastSeen = 0 for all of them. To avoid
 *   spamming the bell, we treat lastSeen=0 as "initialise to now" — we
 *   write the current `updated_at` into localStorage and DON'T surface a
 *   notification. Real new messages on subsequent polls work as expected.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFeature } from '@/shared/features/useFeature';
import { useMyTickets } from '@/modules/support/hooks/useMyTickets';
import { ticketsKeys } from '@/modules/support/hooks/queryKeys';
import type { TicketListItem } from '@/modules/support/types/support';
import {
  getLastSeen,
  setLastSeen,
  useSupportNotificationsStore,
  type SupportNotificationEntry,
} from '@/shared/stores/useSupportNotificationsStore';

/** Statuses that mean "the user might still receive a reply" — closed
 *  tickets shouldn't blink the bell even if metadata gets touched. */
const ACTIVE_STATUSES = new Set<string>([
  'open',
  'in_progress',
  'waiting_customer',
  'reopened',
  'escalated',
]);

/** Polling cadence for the global detector. 30s matches what humans
 *  consider "near real-time" for chat-like replies without flooding the
 *  BFF (`/support/tickets/mine` is paginated + cached at 30s staleTime). */
const POLL_INTERVAL_MS = 30_000;

function truncate(input: string | undefined, max = 60): string {
  if (!input) return '';
  const flat = input.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : flat.slice(0, max - 1) + '…';
}

function toEntry(t: TicketListItem): SupportNotificationEntry {
  // Use the canonical update timestamp so concurrent polls dedupe — the
  // entry id only changes when the ticket actually moves forward.
  const ts = t.updated_at;
  return {
    id: `${t.id}:${ts}`,
    ticketId: t.id,
    shortId: t.short_id,
    subject: t.subject,
    bodyPreview: truncate(t.last_message_preview, 60),
    lastMessageAt: ts,
    detectedAt: Date.now(),
    read: false,
  };
}

/**
 * Mount once at the app level (AppLayout). Returns nothing — side effect
 * is updating `useSupportNotificationsStore`.
 */
export function useNewSupportMessages(): void {
  const { enabled: supportEnabled } = useFeature('support');
  const qc = useQueryClient();
  const upsert = useSupportNotificationsStore((s) => s.upsert);
  const clear = useSupportNotificationsStore((s) => s.clear);

  // useMyTickets is the canonical query — sharing the cache means the
  // support inbox page and the notification poller both consume the same
  // network call instead of duplicating it. We override the refetch
  // interval via React Query so the hook polls every 30s globally.
  const { data } = useMyTickets();

  // Tighten the cadence when the detector is mounted. We do it on the
  // shared cache so any other consumer of `useMyTickets` automatically
  // gets faster updates while the user is online.
  useEffect(() => {
    if (!supportEnabled) return;
    const interval = window.setInterval(() => {
      qc.invalidateQueries({ queryKey: ticketsKeys.mine() });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [supportEnabled, qc]);

  // When the feature flag turns off (tenant downgrade, etc), wipe any
  // stale entries so we don't keep blinking the bell for an inaccessible
  // module.
  useEffect(() => {
    if (!supportEnabled) clear();
  }, [supportEnabled, clear]);

  // Detection pass — runs every time the cached payload changes.
  useEffect(() => {
    if (!supportEnabled) return;
    if (!data?.items?.length) return;

    for (const ticket of data.items) {
      if (!ACTIVE_STATUSES.has(ticket.status)) continue;

      const updatedAtMs = Date.parse(ticket.updated_at);
      if (!Number.isFinite(updatedAtMs)) continue;

      const lastSeen = getLastSeen(ticket.id);

      // First sighting: initialise the mark to "now" so we don't spam the
      // bell on first install. Subsequent updates will be detected normally.
      if (lastSeen === 0) {
        setLastSeen(ticket.id, updatedAtMs);
        continue;
      }

      if (updatedAtMs > lastSeen) {
        upsert(toEntry(ticket));
        // Don't write lastSeen here — leave it stale until the user
        // actually acknowledges (clicks the bell / opens the ticket). That
        // way a quick reload doesn't dismiss the cue.
      }
    }
  }, [data, supportEnabled, upsert]);
}
